import { CoverLetter, Optional, Resume } from '@rio/types'
import { useCallback, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { trackInternalEvent, trackMarketingEvent } from '@rio/tracking'
import startCase from 'lodash/startCase'
import { useAutoTailoringAppConfig } from 'builder/hooks/useAutoTailoringAppConfig'
import { useConfig } from 'builder/hooks/useConfig'
import { APP_BASE_PATH, FORMATS } from 'builder/modules/constants'
import { isResumeDocument } from 'builder/utils/getDocumentType'
import { isEmptySection } from 'builder/modules/resumeEditor/autoTailoringRules'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { selectors as userSelectors } from 'builder/modules/user'
import { shouldOpenPlansPageAbandonment } from 'builder/components/AbandonmentModal/utils'
import { actions as uiActions } from 'builder/modules/ui'
import {
  actions,
  DownloadDocumentHandler,
  isEmptyField,
  SectionNames,
} from 'builder/modules/resumeEditor'
import { actions as renderingActions } from 'builder/modules/rendering'
import { goToBillingPlanPage } from 'builder/utils/goToBillingPlanPage'
import { DOWNLOAD_CURRENT_DOCUMENT_FORMAT_URL_PARAM } from 'builder/modules/resumeEditor/constants'

export const IS_BUILDER_DOWNLOAD_AUTO_TAILOR_MODAL_SHOWN =
  'IS_BUILDER_DOWNLOAD_AUTO_TAILOR_MODAL_SHOWN'

type Props = {
  currentDocument: Optional<Resume | CoverLetter>
  isFromJobTracker?: boolean
  downloadClickEventSource: string
  downloadActionEventSource: string
}

export const useDocumentDownload = (props: Props) => {
  const {
    currentDocument,
    isFromJobTracker = false,
    downloadClickEventSource,
    downloadActionEventSource,
  } = props
  const dispatch = useDispatch()
  const { isAutoTailoringEnabled } = useAutoTailoringAppConfig()
  const config = useConfig()
  const isResume = isResumeDocument(currentDocument)
  const userData = useTypedSelector(userSelectors.userData)
  const downloadButtonActions = config?.features.downloadButtonActions

  const isDataMissed = useMemo(() => {
    if (isResume) {
      const isWorkExperienceEmpty = isEmptySection(currentDocument, SectionNames.workExperiences)
      const isProfileEmpty = isEmptyField(currentDocument, SectionNames.profile)
      return isWorkExperienceEmpty && isProfileEmpty
    } else {
      return false
    }
  }, [currentDocument, isResume])

  const handleDownloadDocument: DownloadDocumentHandler = useCallback(
    params => {
      if (!currentDocument) return

      const { format = FORMATS.pdf } = params || {}
      const { type, template } = currentDocument

      // Track in Google Analytics
      trackMarketingEvent('Preview Modal', `Download ${startCase(type)}`, {
        eventLabel: format,
      })
      // Track in ClickHouse
      trackInternalEvent(`click_download_${type}_button`, {
        format,
        source: downloadClickEventSource,
        template,
        ...(isResume && { score: currentDocument.resumeScore }),
      })

      const wasAutoTailorDownloadModalShown = localStorage.getItem(
        IS_BUILDER_DOWNLOAD_AUTO_TAILOR_MODAL_SHOWN,
      )
      if (
        (downloadButtonActions === 'at_banner_link' || downloadButtonActions === 'at_banner') &&
        isAutoTailoringEnabled &&
        !isDataMissed &&
        isResume &&
        !wasAutoTailorDownloadModalShown
      ) {
        dispatch(actions.setIsCustomizeModalOpen(false))
        if (downloadButtonActions === 'at_banner') {
          dispatch(actions.setFeaturingAutoTailorModalOpenOnDownload(true))
        }
        dispatch(actions.setOpenOptimizerModal(true))
        dispatch(
          actions.setResumeDownloadData({
            id: currentDocument.id,
            type,
            format,
            source: 'builder',
            template,
          }),
        )
        localStorage.setItem(IS_BUILDER_DOWNLOAD_AUTO_TAILOR_MODAL_SHOWN, 'true')
        return
      }
      if (
        window.location.pathname.includes('/resumes') &&
        userData?.registeredAt &&
        shouldOpenPlansPageAbandonment(
          userData.registeredAt,
          config?.features.plansPageAbandonment || false,
          userData.showAbandonmentFlows,
        ) &&
        (downloadButtonActions === 'control' ||
          downloadButtonActions === 'default' ||
          !downloadButtonActions)
      ) {
        trackInternalEvent('show_abandonment_popup', { source: 'dashboard', type: 'plans' })
        dispatch(
          actions.setResumeDownloadData({
            id: currentDocument.id,
            type,
            format,
            source: 'builder',
            template,
          }),
        )
        dispatch(uiActions.openPlansPageAbandonmentModal())
        return
      }

      if (
        !userData?.hasPremiumFeatures &&
        isResumeDocument(currentDocument) &&
        currentDocument.autoTailored
      ) {
        const editorUrl = `${APP_BASE_PATH}/resumes/${currentDocument.id}/edit`
        const successParams = new URLSearchParams({
          [DOWNLOAD_CURRENT_DOCUMENT_FORMAT_URL_PARAM]: format,
        })

        const getSuccessRedirectUrl = () => {
          if (currentDocument.jobCardId) {
            successParams.set('card_id', String(currentDocument.jobCardId))
            return `${APP_BASE_PATH}/job-tracking?${successParams.toString()}`
          }
          return `${editorUrl}?${successParams.toString()}`
        }

        const redirectUrl = getSuccessRedirectUrl()

        goToBillingPlanPage({
          onSuccessRedirect: redirectUrl,
          onCloseRedirect: editorUrl,
          tailoring: true,
        })
        return
      }

      dispatch(
        renderingActions.download({
          id: currentDocument.id,
          type,
          format,
          source: downloadActionEventSource,
          template,
        }),
      )

      if (isFromJobTracker) {
        dispatch(actions.setOpenSavingInfoBanner(true))
        trackInternalEvent('view_save_banner', {
          label: 'optimizer',
        })
      }
    },
    [
      currentDocument,
      dispatch,
      downloadButtonActions,
      isAutoTailoringEnabled,
      isDataMissed,
      isFromJobTracker,
      isResume,
      userData,
      config,
      downloadClickEventSource,
      downloadActionEventSource,
    ],
  )

  return {
    handleDownloadDocument,
  }
}
